import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppModal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
} from "../../components/Common/AppOverlay";
import { getData, postData } from "../../helpers/url-request";
import { BASE_URL } from "../../helpers/url_helper";
import {
  clearAuthenticatedUser,
  broadcastLogout,
} from "../../helpers/auth-session";

export default function ChangeEntraPassword() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [values, setValues] = useState({
    current: "",
    password: "",
    confirm: "",
  });
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    let active = true;
    getData("/user/password/status")
      .then((response) => {
        if (!active) return;
        if (!response.status) throw new Error();
        setStatus(response.data);
        const query = new URLSearchParams(location.search);
        if (query.has("changePassword")) {
          setOpen(true);
          if (query.has("entraError"))
            setError(
              "Microsoft account verification failed or permission has not been granted. Try again, or contact your PIERS administrator."
            );
          query.delete("changePassword");
          query.delete("entraError");
          navigate(
            { pathname: location.pathname, search: query.toString() },
            { replace: true }
          );
        }
      })
      .catch(() => {
        if (active) {
          setStatus({ available: false });
          setError(
            "Unable to check password-change availability. Refresh the page to try again."
          );
        }
      });
    return () => {
      active = false;
    };
  }, [location.pathname, location.search, navigate]);

  const clear = () => setValues({ current: "", password: "", confirm: "" });
  const close = () => {
    if (!busy) {
      clear();
      setOpen(false);
      setError("");
      setSuccess("");
    }
  };
  const verify = () => {
    clear();
    const returnUrl = `${window.location.origin}/profile`;
    window.location.assign(
      `${BASE_URL}/user/password/authorize?returnUrl=${encodeURIComponent(
        returnUrl
      )}`
    );
  };
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (
      !values.current ||
      values.password.length < 8 ||
      values.password.length > 256 ||
      values.password !== values.confirm ||
      values.password === values.current
    ) {
      setError(
        "Enter your current password and matching new passwords of 8–256 characters. The new password must differ from the current password."
      );
      return;
    }
    setBusy(true);
    try {
      const result = await postData("/user/password/change", {
        currentPassword: values.current,
        newPassword: values.password,
        confirmPassword: values.confirm,
      });
      if (result.status) {
        clear();
        setSuccess(result.message);
      } else {
        setError(result.message || "Unable to change your password.");
        if (
          ["reauthentication_required", "password_change_uncertain"].includes(
            result.data?.code
          )
        ) {
          clear();
          setStatus((s) => ({ ...s, authorized: false }));
        }
      }
    } catch {
      clear();
      setError(
        "The password-change response could not be confirmed. Try signing in with your new password before attempting another change."
      );
      setStatus((s) => ({ ...s, authorized: false }));
    } finally {
      setBusy(false);
    }
  };
  const signIn = () => {
    clearAuthenticatedUser();
    broadcastLogout();
    window.location.assign("/signin");
  };
  return (
    <>
      <div className="profile-page__panel-row">
        <i className="bx bx-lock-alt profile-page__detail-icon profile-page__detail-icon--purple" />
        <div>
          <small>Password</small>
          <strong>
            {status?.available
              ? "Managed by Microsoft"
              : "Managed by your sign-in provider"}
          </strong>
        </div>
        <button
          className="btn profile-page__outline-button"
          type="button"
          disabled={!status?.available}
          onClick={() => {
            setError("");
            setOpen(true);
          }}
        >
          Change Password
        </button>
      </div>
      {status && !status.available && (
        <p className="text-muted">
          {error ||
            "For corporate SSO, manage your password with your organization. This flow is available only for PIERS-managed Entra accounts."}
        </p>
      )}
      <AppModal
        isOpen={open}
        toggle={success ? undefined : close}
        backdrop="static"
        labelledBy="change-password-title"
      >
        <ModalHeader
          id="change-password-title"
          toggle={busy || success ? undefined : close}
        >
          Change Password
        </ModalHeader>
        <ModalBody>
          {error && (
            <p role="alert" className="text-danger">
              {error}
            </p>
          )}
          {success ? (
            <p role="status">{success}</p>
          ) : !status?.authorized ? (
            <>
              <p>
                Verify your Microsoft account before changing your password.
                You’ll return here to enter your current and new passwords.
              </p>
              <Button
                color="primary"
                onClick={verify}
                disabled={!status?.available}
              >
                Verify Microsoft account
              </Button>
            </>
          ) : (
            <form id="change-entra-password" onSubmit={submit}>
              {[
                ["current", "Current password", "current-password"],
                ["password", "New password", "new-password"],
                ["confirm", "Confirm new password", "new-password"],
              ].map(([key, label, autocomplete]) => (
                <div className="mb-3" key={key}>
                  <label htmlFor={`password-${key}`} className="form-label">
                    {label}
                  </label>
                  <input
                    className="form-control"
                    id={`password-${key}`}
                    type="password"
                    autoComplete={autocomplete}
                    required
                    maxLength={256}
                    minLength={key === "current" ? undefined : 8}
                    disabled={busy}
                    value={values[key]}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [key]: e.target.value }))
                    }
                  />
                </div>
              ))}
              <p className="text-muted">
                Choose a password that meets Microsoft’s password policy. After
                saving, sign in again with your new password.
              </p>
            </form>
          )}
        </ModalBody>
        <ModalFooter>
          {success ? (
            <Button color="primary" onClick={signIn}>
              Sign in again
            </Button>
          ) : (
            <>
              <Button color="secondary" onClick={close} disabled={busy}>
                Cancel
              </Button>
              {status?.authorized && (
                <Button
                  color="primary"
                  type="submit"
                  form="change-entra-password"
                  disabled={busy}
                >
                  {busy ? "Changing password…" : "Save new password"}
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </AppModal>
    </>
  );
}
